import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

/**
 * /join/:code — handles invite links.
 *  - If not authed: bounce to /login?next=/join/<code>
 *  - If authed: POST /api/lobbies/:code/join then redirect to /lobby/:code
 *  - Shows clear error if full / not found / already in another lobby
 */
export default function JoinLobby() {
  const { code } = useParams();
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [err, setErr] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (loading || ran.current) return;
    ran.current = true;
    if (!isAuthenticated) {
      navigate(`/login?next=${encodeURIComponent(`/join/${code}`)}`, { replace: true });
      return;
    }
    (async () => {
      try {
        await api.post(`/lobbies/${code}/join`);
        navigate(`/lobby/${code}`, { replace: true });
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 404) setErr("That lobby doesn't exist anymore.");
        else if (status === 409) setErr(detail || "Cannot join this lobby.");
        else setErr(detail || "Could not join the lobby.");
      }
    })();
  }, [code, isAuthenticated, loading, navigate]);

  return (
    <div data-testid="join-page" className="min-h-screen bg-[var(--bg)] text-zinc-100 flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        {err ? (
          <>
            <h1 className="text-3xl font-bold">Can&apos;t join lobby</h1>
            <p className="mt-3 text-sm text-zinc-400" data-testid="join-error">
              {err}
            </p>
            <div className="text-xs text-zinc-500 mt-2">Code: {code}</div>
            <Link
              to="/dashboard"
              data-testid="join-back"
              className="mt-6 inline-block px-5 py-2.5 rounded-full bg-[var(--accent)] text-black font-semibold"
            >
              Back to dashboard
            </Link>
          </>
        ) : (
          <>
            <div className="text-xs uppercase tracking-[0.4em] text-[var(--accent)]">joining</div>
            <h1 className="text-3xl font-bold mt-3">Hopping into lobby {code}…</h1>
            <p className="mt-2 text-sm text-zinc-400">One second.</p>
          </>
        )}
      </div>
    </div>
  );
}
